import { useNavigate } from "react-router-dom";
import { Bot } from "lucide-react";
import { Area, AreaChart, Bar as RBar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Disclaimer, Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { useWsData } from "@/hooks/useWsData";
import { brl, brlShort } from "@/lib/api";

const Tip = { contentStyle: { background: "#0B1A33", border: "1px solid #1E3A6E", borderRadius: 8 }, formatter: (v) => brl(v) };

const Strategies = ({ items }) => (
  <ul className="space-y-2 text-sm">{items.map((s) => <li key={s} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" />{s}</li>)}</ul>
);

function Projection({ proj, strategies, label, testid }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid={testid}>
      <Section title={`Projeção ${label}`} className="lg:col-span-2">
        <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-xs text-slate-400">Patrimônio projetado (nominal)</div><div className="num text-xl text-[#F5D77F]" data-testid={`${testid}-total`}>{brl(proj.total)}</div></div>
          <div><div className="text-xs text-slate-400">Em valores de hoje</div><div className="num text-xl text-emerald-300">{brl(proj.points.at(-1)?.real)}</div></div>
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={proj.points}>
              <CartesianGrid stroke="#1E3A6E" strokeDasharray="3 3" />
              <XAxis dataKey="year" stroke="#64748B" fontSize={11} tickFormatter={(y) => `${y}a`} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={brlShort} width={70} />
              <Tooltip {...Tip} /><Legend />
              <Area dataKey="nominal" name="Nominal" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.15} strokeWidth={2} />
              <Area dataKey="real" name="Real (descontada inflação)" stroke="#10B981" fill="#10B981" fillOpacity={0.12} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>
      <Section title="Estratégia"><Strategies items={strategies} /></Section>
    </div>
  );
}

export default function Planejamento() {
  const { data: p } = useWsData("/plan");
  const nav = useNavigate();
  if (!p) return <Spinner />;
  const a = p.assumptions;
  return (
    <div data-testid="planejamento-page">
      <PageHeader eyebrow="Estratégia" title="Planejamento personalizado" subtitle="Plano por horizonte, calculado pelo motor determinístico PROSPERA+ a partir do seu diagnóstico.">
        <Button onClick={() => nav("/app/mentor?prompt=estrategia")} data-testid="ai-strategy-btn"><Bot className="mr-1 h-4 w-4" /> Gerar estratégia com Mentor IA</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Aporte mensal possível" value={brl(a.monthly_contribution)} tone="green" testid="plan-contribution" />
        <Kpi label="Taxa nominal (premissa)" value={`${a.annual_rate_pct}% a.a.`} tone="gold" />
        <Kpi label="Inflação (premissa)" value={`${a.inflation_pct}% a.a.`} />
        <Kpi label="Patrimônio líquido atual" value={brl(p.net_worth)} tone="gold" />
      </div>
      <Tabs defaultValue="12m">
        <TabsList className="mb-6 bg-[#0B1A33]">
          <TabsTrigger value="12m" data-testid="tab-12m">12 meses</TabsTrigger>
          <TabsTrigger value="5a" data-testid="tab-5a">5 anos</TabsTrigger>
          <TabsTrigger value="30a" data-testid="tab-30a">30 anos</TabsTrigger>
        </TabsList>
        <TabsContent value="12m">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="plan-12m">
            <Section title="Caixa acumulado vs. meta de reserva (6 meses de despesas)" className="lg:col-span-2">
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={p.twelve_months}>
                    <CartesianGrid stroke="#1E3A6E" strokeDasharray="3 3" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={11} tickFormatter={(m) => `M${m}`} />
                    <YAxis stroke="#64748B" fontSize={11} tickFormatter={brlShort} width={70} />
                    <Tooltip {...Tip} />
                    <RBar dataKey="cash_projection" name="Caixa projetado" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <RBar dataKey="reserve_target" name="Meta de reserva" fill="#D4AF37" fillOpacity={0.35} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
            <Section title="Estratégia de curto prazo"><Strategies items={p.strategies.curto} /></Section>
          </div>
        </TabsContent>
        <TabsContent value="5a"><Projection proj={p.five_years} strategies={p.strategies.medio} label="5 anos" testid="plan-5a" /></TabsContent>
        <TabsContent value="30a"><Projection proj={p.thirty_years} strategies={p.strategies.longo} label="30 anos" testid="plan-30a" /></TabsContent>
      </Tabs>
      <Disclaimer text={p.disclaimer} />
    </div>
  );
}
